<script lang="ts">
export default {
  name: 'ExchangeBottomTabs',
  customOptions: {},
};
</script>

<script lang="ts" setup>
import { ToggleInputCustomEvent } from '@1trading/design-system';
import { orderBy } from 'lodash/fp';
import { isMobile } from 'mobile-device-detect';
import { computed, onBeforeUnmount, onMounted, ref, watch, type ComputedRef, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import { partnersService } from '@exchange/libs/partners/service/src';

import { tabs as bottomNavTabs, type TabValues, type TabInfo, type TabKeys } from './bottom-tabs';

interface BottomTabInfo extends TabInfo {
  isActive: ComputedRef<boolean>;
  translation: ComputedRef<string>;
  showGreenDot: ComputedRef<boolean>;
}

const props = defineProps({
  activeTab: { type: String as PropType<TabKeys>, required: true },
  bottomTabs: { type: Object as PropType<Record<TabKeys, TabValues>>, required: true },
});
const emit = defineEmits<{
  (e: 'bottom-tab:clicked', key: TabValues): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const { showPartnersDot } = partnersService;

const showGreenDot = computed(() => showPartnersDot.value);

const getTabsInfo = () => {
  const ts = Object.values(props.bottomTabs).reduce((acc, tab) => {
    const isActive = computed(() => props.activeTab === tab.key);
    const tabGreenDot = computed(() => [bottomNavTabs.PROFILE.key].some((key) => key === tab.key) && showGreenDot.value);

    acc.push({
      ...tab,
      translation: computed(() => t(`modules.ExchangeNavigationTabs.${tab.key}`)),
      isActive,
      showGreenDot: tabGreenDot,
    });

    return acc;
  }, [] as Array<BottomTabInfo>);

  return orderBy(['order'], ['asc'], ts);
};

const tabs = ref(getTabsInfo());
watch(
  () => props.bottomTabs,
  () => {
    tabs.value = getTabsInfo();
  },
);
const onTabClick = (tabKey: TabValues) => {
  emit('bottom-tab:clicked', tabKey);
};

const useToggleBottomNav = () => {
  const keyboardIsVisible = ref(false);

  const onInputStateChange = (event: ToggleInputCustomEvent) => {
    keyboardIsVisible.value = event.detail.focused;
  };

  onMounted(() => {
    window.addEventListener(ToggleInputCustomEvent.type, onInputStateChange as EventListener, true);
  });

  onBeforeUnmount(() => {
    window.removeEventListener(ToggleInputCustomEvent.type, onInputStateChange as EventListener, true);
  });

  const shouldBottomTabsBeVisible = computed(() => !keyboardIsVisible.value || !isMobile);

  const shouldBottomTabsBeVisibleDelayed = ref(shouldBottomTabsBeVisible.value);

  watch(
    shouldBottomTabsBeVisible,
    (value) => {
      if (value) {
        setTimeout(() => {
          shouldBottomTabsBeVisibleDelayed.value = true;
        }, 100);
      } else {
        shouldBottomTabsBeVisibleDelayed.value = false;
      }
    },
    { immediate: true },
  );

  return {
    shouldBottomTabsBeVisibleDelayed,
  };
};
const { shouldBottomTabsBeVisibleDelayed } = useToggleBottomNav();
</script>

<template>
  <div
    v-show="shouldBottomTabsBeVisibleDelayed"
    class="exchange-bottom-tabs"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      :class="['exchange-bottom-tabs__button', { 'exchange-bottom-tabs__button--active': tab.isActive }]"
      @click="onTabClick(tab)"
    >
      <ot-icon
        :key="tab.icon"
        class="exchange-bottom-tabs__button-icon"
        :name="tab.icon"
        :inherit-stroke="true"
        :class="[`test-${tab.key}`, 'exchange-bottom-tabs__button-icon', { 'exchange-bottom-tabs__button-icon--show-green-dot': tab.showGreenDot }]"
      />
      <div class="exchange-bottom-tabs__button-text">{{ tab.translation }}</div>
    </button>
  </div>
</template>

<style lang="scss">
.exchange-bottom-tabs {
  display: flex;
  width: 100%;
  height: calc(var(--app-mobile-bottom-tabs-height) + var(--safe-area-bottom, 0));
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  padding-bottom: var(--safe-area-bottom, 0);
  background-color: rgb(var(--v-theme-elevation-0));

  .is-web .xs & {
    padding-bottom: calc(var(--safe-area-bottom, 0) / 2);
  }

  &__button {
    z-index: 1;
    display: flex;
    width: 52px;
    height: 44px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4px 16px;
    border-radius: 8px;
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-xxs);
    gap: 6px;
    outline: none;
    user-select: none;

    &--active {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__button-icon {
    position: relative;
    display: block;
    width: 24px;
    height: 24px;
    justify-content: center;

    &--show-green-dot::after {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 10px;
      height: 10px;
      border: solid 2px rgb(var(--v-theme-elevation-1));
      border-radius: 100%;
      background-color: rgb(var(--v-theme-primary));
      content: '';
    }
  }
}
</style>
